'use client';

import { useCallback, useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../../components/Footer';
import PropertyCard from '../../../../components/PropertyCard';
import FilterPanel from '../../../../components/FilterPanel';
import HorizontalFilterBar from '../../../../components/HorizontalFilterBar';
import AdBanner from '../../../../components/AdBanner';
import ExpertHelpCard from '../../../../components/ExpertHelpCard';
import MobileListingTopBar from '../../../../components/MobileListingTopBar';
import useCheckLogin from '../../../../components/CheckLogin';
import { api } from '@/lib/apiClient';
import { useSearchParams, useRouter } from 'next/navigation';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';
const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'newest', label: 'Newest First' },
    { value: 'rent_low', label: 'Rent: Low to High' },
    { value: 'rent_high', label: 'Rent: High to Low' },
];

function convertToArray(str) {
    if (!str) return [];
    return str.split('-').map(num => parseInt(num, 10));
}

function PropertyCardSkeleton() {
    return (
        <div className="relative p-2 md:p-3 bg-white border border-slate-100 rounded-2xl md:rounded-[20px] shadow-xs flex flex-row gap-3 md:gap-6 items-center animate-pulse">
            <div className="relative w-[110px] h-[82px] xs:w-[125px] xs:h-[94px] md:w-60 md:h-40 bg-slate-200 rounded-xl md:rounded-2xl shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col h-full justify-between py-1">
                <div>
                    <div className="flex justify-between items-start">
                        <div className="h-6 w-32 bg-slate-200 rounded-md"></div>
                        <div className="w-7 h-7 rounded-full bg-slate-200 shrink-0"></div>
                    </div>
                    <div className="h-4 w-3/4 bg-slate-200 rounded-md mt-2.5"></div>
                    <div className="h-3 w-1/2 bg-slate-200 rounded-md mt-2"></div>
                </div>
                <div className="hidden md:grid grid-cols-3 gap-2 mt-4 bg-slate-50 rounded-xl p-2.5 border border-slate-100">
                    <div className="h-8 bg-slate-200/60 rounded"></div>
                    <div className="h-8 bg-slate-200/60 rounded"></div>
                    <div className="h-8 bg-slate-200/60 rounded"></div>
                </div>
                <div className="flex md:hidden gap-3 mt-2">
                    <div className="h-3 w-12 bg-slate-200 rounded"></div>
                    <div className="h-3 w-12 bg-slate-200 rounded"></div>
                </div>
            </div>
        </div>
    );
}

export default function RentListingPage() {
    const isLoggedIn = useCheckLogin();
    const [filters, setFilters] = useState({});
    const [sortBy, setSortBy] = useState('relevance');
    const [properties, setProperties] = useState([]);
    const [leaseRentFilter, setLeaseRentFilter] = useState('all');
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [visibleCount, setVisibleCount] = useState(3);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isFilterLoading, setIsFilterLoading] = useState(false);
    const filterTimeoutRef = useRef(null);
    const [viewMode, setViewMode] = useState('grid');
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
    const searchParams = useSearchParams();
    const router = useRouter();
    const cityId = searchParams.get("cityId");
    const [city_name, setCityName] = useState("");
    const [localities, setLocalities] = useState("");
    const [parms, setParms] = useState("");
    const observerRef = useRef(null);
    const sentinelRef = useRef(null);

    useEffect(() => {
        return () => {
            if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
        };
    }, []);

    // Read localities from query params if any are present
    const localitiesParam = searchParams.get("localities");
    const initialLocalities = localitiesParam ? localitiesParam.split('-').map(num => parseInt(num, 10)) : [];

    const [localitiesIds, setLocalitiesIds] = useState(initialLocalities);

    useEffect(() => {
        fetchProperties();

    }, [searchParams]);

    useEffect(() => {
        let filtered = [...properties];

        // Property Type
        if (filters.propertyType?.length) {
            filtered = filtered.filter(p =>
                filters.propertyType.includes(p.type?.toString().toLowerCase())
            );
        }

        // BHK
        if (filters.bhk?.length) {
            filtered = filtered.filter(p => {
                const bedrooms = parseInt(p.bedrooms, 10);
                if (isNaN(bedrooms)) return false;
                return filters.bhk.some(val => {
                    if (val === '3+') return bedrooms >= 3;
                    if (val === '5+') return bedrooms >= 5;
                    return bedrooms?.toString() === val;
                });
            });
        }

        // Facing
        if (filters.facing?.length) {
            filtered = filtered.filter(p => {
                const face = p.facing?.toLowerCase().replace(',', '-').replace(' ', '') || '';
                const normalizedFilters = filters.facing.map(f => f.toLowerCase().replace(',', '-').replace(' ', ''));
                return normalizedFilters.some(f => {
                    return (face === f) || (!f.includes('-') && face.split('-').includes(f));
                });
            });
        }

        // Budget
        if (filters.budgetMin !== undefined) {
            filtered = filtered.filter(p => {
                let actualPrice = p.price;

                // Convert formatted values back to absolute ₹ values for rent comparison
                if (p.priceUnit === 'Lac') {
                    actualPrice = p.price * 100000;
                } else if (p.priceUnit === 'Cr') {
                    actualPrice = p.price * 10000000;
                }

                return (
                    actualPrice >= filters.budgetMin &&
                    (filters.budgetMax === null || actualPrice <= filters.budgetMax)
                );
            });
        }

        // Furnishing
        if (filters.furnishing?.length) {
            filtered = filtered.filter(p => {
                const f = p.furnishing?.toLowerCase() || '';
                return filters.furnishing.some(opt => {
                    if (opt === 'semi') return f.includes('semi');
                    if (opt === 'unfurnished') return f.includes('unfurnished');
                    if (opt === 'furnished') return f.includes('furnished') && !f.includes('semi') && !f.includes('unfurnished');
                    return false;
                });
            });
        }

        // Tenant Preference
        if (filters.tenantPreference?.length) {
            filtered = filtered.filter(p => {
                const t = (p.tenantPreference || p.tenants_preference || '').toLowerCase();
                return filters.tenantPreference.some(opt => {
                    const normalizedOpt = opt.toLowerCase();
                    if (normalizedOpt.startsWith('bachelor')) return t.includes('bachelor');
                    if (normalizedOpt === 'family') return t.includes('family');
                    if (normalizedOpt === 'company' || normalizedOpt === 'commercial') return t.includes('company') || t.includes('commercial');
                    return false;
                });
            });
        }

        // Availability
        if (filters.availability?.length) {
            filtered = filtered.filter(p => {
                const status = p.possessionStatus?.toLowerCase() || ''; // 'active' or 'pending'
                const type = p.availability?.toLowerCase() || ''; // 'ready to occupy' or 'available later' or null

                const isReady = status === 'active' || type.includes('ready') || type.includes('immediate');
                const isLater = status === 'pending' || type.includes('later') || type.includes('pending');

                return filters.availability.some(opt => {
                    if (opt === 'immediate' || opt === 'active' || opt === '15_days' || opt === '30_days') {
                        return isReady;
                    }
                    if (opt === 'pending') {
                        return isLater;
                    }
                    return false;
                });
            });
        }

        // Posted By
        if (filters.postedBy?.length) {
            filtered = filtered.filter(p => {
                const posted = p.postType?.toLowerCase() || '';
                return filters.postedBy.some(opt => {
                    const normalizedOpt = opt === 'owner' ? 'seller' : opt;
                    return posted.includes(normalizedOpt);
                });
            });
        }

        // Rent vs Lease filter
        if (leaseRentFilter === 'rent') {
            filtered = filtered.filter(p => {
                const isLease = p.listingType?.toLowerCase().includes('rent');
                return isLease;
            });
        } else if (leaseRentFilter === 'lease') {
            filtered = filtered.filter(p => {
                const isLease = p.listingType?.toLowerCase().includes('lease')
                return isLease;
            });
        }
        else {
            filtered = filtered.filter(p => {
                const isLease = p.listingType?.toLowerCase().includes('rent') || p.listingType?.toLowerCase().includes('lease')
                return isLease;
            });
        }

        // Sort properties
        const getNumericPrice = (p) => {
            let price = parseFloat(p.price) || 0;
            if (p.priceUnit === 'Lac') {
                return price * 100000;
            } else if (p.priceUnit === 'Cr') {
                return price * 10000000;
            }
            return price;
        };

        if (sortBy === 'rent_low') {
            filtered.sort((a, b) => getNumericPrice(a) - getNumericPrice(b));
        } else if (sortBy === 'rent_high') {
            filtered.sort((a, b) => getNumericPrice(b) - getNumericPrice(a));
        } else if (sortBy === 'newest') {
            filtered.sort((a, b) => {
                const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
                const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
                if (timeA && timeB) {
                    return timeB - timeA;
                }
                const idA = parseInt(a.id, 10) || 0;
                const idB = parseInt(b.id, 10) || 0;
                return idB - idA;
            });
        }

        setFilteredProperties(filtered);
        setVisibleCount(3);
        setIsLoadingMore(false);
    }, [filters, properties, leaseRentFilter, sortBy]);

    const hasMore = visibleCount < filteredProperties.length;
    const visibleProperties = filteredProperties.slice(0, visibleCount);

    const stateRef = useRef({
        hasMore,
        loading,
        isFilterLoading,
        isLoadingMore,
    });
    useEffect(() => {
        stateRef.current = {
            hasMore,
            loading,
            isFilterLoading,
            isLoadingMore,
        };
    }, [hasMore, loading, isFilterLoading, isLoadingMore]);

    const handleLoadMore = useCallback(() => {
        if (stateRef.current.isLoadingMore || !stateRef.current.hasMore) return;
        setIsLoadingMore(true);
        stateRef.current.isLoadingMore = true;
        setTimeout(() => {
            setVisibleCount(prev => prev + 3);
            setIsLoadingMore(false);
            stateRef.current.isLoadingMore = false;
        }, 400);
    }, []);

    // Callback ref for bottom sentinel
    const setLoadMoreTriggerRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        sentinelRef.current = node;
        if (!node) return;
        if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (first?.isIntersecting) {
                    const { hasMore, loading, isFilterLoading, isLoadingMore } = stateRef.current;
                    if (hasMore && !loading && !isFilterLoading && !isLoadingMore) {
                        handleLoadMore();
                    }
                }
            },
            {
                root: null,
                rootMargin: '250px',
                threshold: 0.05,
            }
        );
        observer.observe(node);
        observerRef.current = observer;
    }, [handleLoadMore]);

    // Cleanup observer on unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    // Window scroll and resize fallback for reliable bottom detection across all mobile and desktop devices
    useEffect(() => {
        const handleScroll = () => {
            const { hasMore, loading, isFilterLoading, isLoadingMore } = stateRef.current;
            if (!hasMore || loading || isFilterLoading || isLoadingMore) return;

            const docEl = typeof document !== 'undefined' ? document.documentElement : null;
            const docBody = typeof document !== 'undefined' ? document.body : null;
            const scrollHeight = Math.max(docEl?.scrollHeight || 0, docBody?.scrollHeight || 0);
            const scrollTop = window.scrollY || window.pageYOffset || docEl?.scrollTop || docBody?.scrollTop || 0;
            const clientHeight = window.innerHeight || docEl?.clientHeight || 0;

                if (scrollHeight > 0 && scrollTop + clientHeight >= scrollHeight - 350) {
                    handleLoadMore();
                }
            };

            window.addEventListener('scroll', handleScroll, { passive: true });
            window.addEventListener('resize', handleScroll, { passive: true });
            return () => {
                window.removeEventListener('scroll', handleScroll);
                window.removeEventListener('resize', handleScroll);
            };
        }, [handleLoadMore]);

    const fetchProperties = async () => {
        try {
            setLoading(true);
            setParms(searchParams.toString());
            const localitiesArray = convertToArray(searchParams.get("localities"));
            setLocalitiesIds(localitiesArray);
            let whereClause = "listing_type IN ('rent', 'lease') and city_id=$1";
            let params = [cityId];

            const res = await api.post('selectwithout/view_home_cities', { sqlData: "*", where: "id=$1", whereData: [cityId] });
            //console.log("res.data", res.data);
            let city_name = res?.data?.[0]?.name || '';
            setCityName(city_name);
            if (localitiesArray && localitiesArray.length > 0) {
                const res1 = await api.post('selectwithout/master_localities', {
                    sqlData: "locality_name as name",
                    where: "id = ANY($1)",
                    whereData: [localitiesArray],
                });
                if (res1.success === 1 && Array.isArray(res1.data)) {
                    let localitiesNames = "";
                    for (let i = 0; i < res1.data.length; i++) {
                        localitiesNames += res1.data[i].name + ", ";
                    }
                    setLocalities(localitiesNames);
                }
            } else {
                setLocalities("");
            }

            if (localitiesArray && localitiesArray.length > 0) {
                whereClause += " and locality_id = ANY($2)";
                params.push(localitiesArray);
            }

            whereClause += " order by created_at desc";

            const response = await api.post('selectwithout/view_home_listings',
                {
                    sqlData: "*",
                    where: whereClause,
                    whereData: params,
                });
            console.log("response.data", response.data);
            let property_card = [];
            if (response?.data && Array.isArray(response.data)) {
                for (let i = 0; i < response.data.length; i++) {
                    const card = response.data[i].property_card;
                    if (card) {
                        property_card.push({
                            ...card,
                            id: response.data[i].id || card.id,
                            created_at: response.data[i].created_at || card.created_at || null
                        });
                    }
                }
            }
            setProperties(property_card);
        } catch (error) {
            console.error('Error fetching properties:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = useCallback((newFilters) => {
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
        }
        setIsFilterLoading(true);
        setFilters(newFilters);
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        filterTimeoutRef.current = setTimeout(() => {
            setIsFilterLoading(false);
        }, 350);
    }, []);

    const handleSave = useCallback((propertyId) => {
        console.log('Save property:', propertyId);
    }, []);

    return (
        <>
            <Navbar />
            <MobileListingTopBar
                filters={filters}
                onFilterChange={handleFilterChange}
                listingType="rent"
                onSearch={(city, locs) => {
                    const params = new URLSearchParams();
                    if (city?.id) params.set('cityId', city.id);
                    if (locs?.length > 0) params.set('localities', locs.map(l => l.id).join('-'));
                    const citySlug = city?.name?.toLowerCase() || 'all';
                    router.push(`/rent/${citySlug}?${params.toString()}`);
                }}
            />

            <main className="min-h-screen bg-gray pb-20 lg:pb-0">
                {/* Horizontal Filter Bar - Sticky with subtle backdrop */}
                <div className="hidden lg:block sticky top-16 z-40 horizontal-filter-bar">
                    <HorizontalFilterBar
                        filters={filters}
                        onChange={handleFilterChange}
                        listingType="rent"
                    />
                </div>

                {/* Header */}
                <div className="bg-gray">
                    <div className="container-custom py-4">
                        <header className="flex mt-5 flex-col md:flex-row md:items-center md:justify-between gap-3">
                            {/* Left: breadcrumb + title */}
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-3">
                                <nav className="flex items-center gap-2 text-sm text-gray-500" aria-label="Breadcrumb">
                                    <span aria-current="page" className="text-gray-900 font-medium">
                                        {loading || isFilterLoading ? (
                                            <span className="text-gray-500 flex items-center gap-2">
                                                <span className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
                                                Updating properties in {city_name || 'selected city'}...
                                            </span>
                                        ) : (
                                            <>
                                                <strong> Available {filteredProperties.length} Properties </strong> for Rent in {localities} {city_name}
                                            </>
                                        )}
                                    </span>
                                </nav>
                            </div>
                            {/* RIGHT – Relevance Select */}
                            <div className="flex mb-3 items-center justify-between md:justify-end gap-2 w-full md:w-auto">
                                <label htmlFor="sortBy" className="text-sm text-gray-600 whitespace-nowrap">
                                    Sort by:
                                </label>
                                <select
                                    id="sortBy"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="h-9 w-full md:w-auto text-sm px-2 rounded-md bg-white border border-gray-300 text-gray-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                >
                                    {sortOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </header>
                    </div>
                </div>

                {/* ---------- Listings + Right Sidebar (Buy-style layout) ---------- */}
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-0 items-start pb-40">

                        {/* Left: Property Grid/List (8 cols) */}
                        <section className="lg:col-span-8 flex flex-col gap-3" aria-labelledby="rent-list-heading">
                            <h2 id="rent-list-heading" className="sr-only">Rental property listings</h2>

                            {loading || isFilterLoading ? (
                                <div className="flex flex-col gap-3">
                                    {[1, 2, 3].map((n) => (
                                        <PropertyCardSkeleton key={n} />
                                    ))}
                                </div>
                            ) : filteredProperties.length === 0 ? (
                                <div className="bg-white rounded-2xl p-10 text-center border border-slate-100 shadow-xs flex flex-col items-center justify-center">
                                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-2xl mb-3">
                                        🏡
                                    </div>
                                    <h3 className="text-base font-bold text-slate-800 mb-1">
                                        No Rental Properties Found
                                    </h3>
                                    <p className="text-xs text-slate-500 max-w-sm mb-4">
                                        We couldn't find any properties matching your selected criteria in {city_name || 'this city'}. Try adjusting your filters.
                                    </p>
                                    {Object.keys(filters).length > 0 && (
                                        <button
                                            onClick={() => handleFilterChange({})}
                                            className="px-4 py-2 text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors cursor-pointer"
                                        >
                                            Reset All Filters
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {visibleProperties.map((property) => (
                                        <PropertyCard
                                            key={property.id}
                                            property={property}
                                            params={parms}
                                            listingType='rent'
                                            onSave={handleSave}
                                            variant="horizontal"
                                        />
                                    ))}

                                    {/* Bottom Sentinel & Loading indicator on scroll */}
                                    {hasMore && (
                                        <div ref={setLoadMoreTriggerRef} className="py-4 flex flex-col items-center justify-center gap-3">
                                            {isLoadingMore ? (
                                                <>
                                                    <div className="w-full flex flex-col gap-3">
                                                        <PropertyCardSkeleton />
                                                    </div>
                                                    <div className="flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white border border-slate-200/80 shadow-xs text-slate-600 text-xs font-medium">
                                                        <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
                                                        <span>Loading more properties...</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="h-6 w-full flex items-center justify-center">
                                                    <span className="text-[11px] text-slate-400">Scroll down to load more</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!hasMore && filteredProperties.length > 3 && (
                                        <div className="py-6 text-center">
                                            <p className="text-xs font-medium text-slate-400 flex items-center justify-center gap-1.5">
                                                <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                You've viewed all {filteredProperties.length} properties
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </section>

                        {/* Right: Ad Sidebar (4 cols) */}
                        <aside className="lg:col-span-4 hidden lg:flex flex-col gap-6 sticky top-24">
                            {/* Featured / Ad Widget */}
                            <ExpertHelpCard
                                title="Add Rental Property ?"
                                description="List your property for rent and reach potential tenants."
                                buttonText="Add Rental Property"
                                link={isLoggedIn ? "/seller/post-property" : "/register"}
                            />

                            {/* Ad Banners */}
                            <AdBanner
                                type={"rent"}
                                noofads={2}
                            />
                        </aside>

                    </div>
                </div>
                {/* ---------- end Listings + Sidebar ---------- */}


                {/* Mobile Filter Modal (accessible basics) */}
                {isMobileFilterOpen && (
                    <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Filters">
                        <div
                            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                            onClick={() => setIsMobileFilterOpen(false)}
                            aria-hidden="true"
                        />
                        <div className="absolute right-0 top-0 bottom-0 w-[80%] bg-white overflow-y-auto shadow-2xl z-10">
                            <FilterPanel
                                filters={filters}
                                onChange={handleFilterChange}
                                listingType="rent"
                                isMobile={true}
                                onClose={() => setIsMobileFilterOpen(false)}
                            />
                        </div>
                    </div>
                )}

                {/* Floating Bottom Filter Panel */}
                <div className="fixed bottom-20  lg:bottom-10 left-1/2 -translate-x-1/2 z-50 flex justify-center w-full max-w-xl px-4">
                    <div className="bg-white/85 backdrop-blur-md border border-black-200/80 rounded-full p-1.5 flex items-center gap-1 shadow-[0_15px_30px_-5px_rgba(0,0,0,0.15),_inset_0_1px_2px_rgba(255,255,255,1)] ring-1 ring-black/5">
                        {[
                            { id: 'all', label: 'Show All', mobileLabel: 'All' },
                            { id: 'rent', label: 'Show Only Rental Properties', mobileLabel: 'Rental Only' },
                            { id: 'lease', label: 'Show Only Lease Properties', mobileLabel: 'Lease Only' }
                        ].map((tab) => {
                            const isActive = leaseRentFilter === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setLeaseRentFilter(tab.id)}
                                    className={`relative px-5 py-2.5 text-xs font-semibold rounded-full transition-colors duration-200 cursor-pointer focus-visible:outline-2 focus-visible:outline-indigo-600 ${isActive ? 'text-white font-bold' : 'text-slate-600 hover:text-slate-900'
                                        }`}
                                >
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeTabIndicator"
                                            className="absolute inset-0 bg-indigo-600 rounded-full shadow-[0_2px_8px_rgba(79,70,229,0.35)] -z-10"
                                            transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                        />
                                    )}
                                    <span className="hidden md:inline">{tab.label}</span>
                                    <span className="inline md:hidden">{tab.mobileLabel}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </main>

            <Footer />
        </>
    );
}
